import { LitElement, html, css } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';

import { DeliveryOverTime, ProductStockView } from '../../types/types';
import { getQueryParamURL, getSearchParam } from '../../util/list_util';
import '../../elements/langhout-button/langhout-button';
import '../../elements/string-array-input/string-array-input';
import {
  authenticatedAPICall,
  isSuccessfulAPIResponse,
} from '../../client/api-client';
import Chart from 'chart.js/auto';
import '../../elements/langhout-checkbox/langhout-checkbox';

import {
  ProductStockTypeOnRequest,
  ProductStockTypeStock,
  roundUnitNumbers,
  StockStatusInStock,
  StockStatusLimited,
} from '../../util/util';
import { LanghoutCheckbox } from '../../elements/langhout-checkbox/langhout-checkbox';
import { showNotification } from '../../LanghoutBetonPortal';
import { closeIcon } from '../../elements/icons/icons';
import { StringArrayInput } from '../../elements/string-array-input/string-array-input';
import { sharedStyles } from '../../elements/shared-styles';

type LabelsAndData = {
  labels: string[];
  data: number[];
  dates: Date[];
};

@customElement('langhout-beton-view-stock')
export class ViewStock extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        font-size: 17px;
        color: var(--text-color-main);
        height: calc(100vh - 84px);
        max-height: calc(100vh - 84px);
        overflow-y: auto;
        background-color: white;
        z-index: 100;
        padding: 10px;
        padding-left: 30px;
        padding-right: 30px;
      }

      :host([disableButtons]) {
        pointer-events: none;
        opacity: 0.7;
      }

      .row {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-grow: 1;
      }

      .centered {
        justify-content: center;
      }

      .inner-wrapper {
        max-width: 900px;
        flex-grow: 1;
      }

      .properties-column-wrapper {
        flex-wrap: wrap;
        flex-flow: row wrap;
      }

      .properties-column {
        width: calc(50%-10px);
        min-width: 250px;
        padding-left: 10px;
      }

      .centered-column {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .wrapper {
        padding-bottom: 400px;
        display: flex;
        flex-direction: row;
        justify-content: center;
      }

      .close-wrapper {
        padding: 5px;
      }

      .label {
        text-align: left;
        min-width: 180px;
        max-width: 180px;
        font-weight: bold;
        flex-grow: 1;
      }

      .value {
        text-align: right;
        min-width: 100px;
        flex-grow: 1;
        max-width: 300px;
      }

      .row-inner-wrapper {
        display: flex;
        flex-direction: row;
      }

      langhout-button {
        max-width: 150px;
        min-width: 140px;
        margin-right: 20px;
        margin-top: 20px;
      }

      .button-row {
        flex-wrap: wrap;
        flex-flow: row wrap;
      }

      .dot {
        height: 25px;
        width: 25px;
        min-width: 25px;
        border-radius: 50%;
        display: inline-block;
        margin-right: 10px;
      }

      .dot-red {
        background-color: red;
      }

      .dot-orange {
        background-color: orange;
      }

      .dot-green {
        background-color: var(--primary-color);
      }

      .display-props {
        margin-bottom: 10px;
      }

      .stock-display-margin {
        margin-bottom: 7px;
      }
    `,
  ];

  @property({ type: Boolean, reflect: true })
  private disableButtons = false;

  @property({ type: Object, reflect: true })
  public currentViewStock: ProductStockView = <ProductStockView>(<unknown>{
    sales_order_limit: [],
  });

  private currentID = '';

  @query('#stockChart')
  public chartCanvas?: HTMLCanvasElement;

  @query('#stockTypeCheckbox')
  private stockTypeCheckbox?: LanghoutCheckbox;

  @query('#limitedStockAmount')
  private limitedStockInput?: HTMLInputElement;

  @query('#notInStockAmount')
  private outOfStockInput?: HTMLInputElement;

  @query('#salesOrderRestrictions')
  private salesOrderLimitElement?: StringArrayInput;

  private chart?: Chart;

  constructor() {
    super();
    this.currentID = getSearchParam(getQueryParamURL(), 'id');
    this.getProduct();
  }

  render() {
    return html`
      <div class="row">
        <div class="fill"></div>
        <div
          class="close-wrapper"
          @click="${() => {
            window.history.back();
          }}"
        >
          ${closeIcon()}
        </div>
      </div>
      <div class="wrapper">
        <div class="inner-wrapper">
          <div class="row centered">
            <h2>Artikel</h2>
          </div>
          <div class="row centered">
            <h3>${this.currentViewStock.description}</h3>
          </div>
          <div class="row properties-column-wrapper">
            <div class="properties-column">
              <div class="row centered display-props">
                <div class="label">Code:</div>
                <div class="value">${this.currentViewStock.code}</div>
              </div>
              <div class="row centered display-props">
                <div class="label">Actuele voorraad:</div>
                <div class="value">
                  ${roundUnitNumbers(
                    this.currentViewStock.stock_amount,
                  ).toLocaleString('nl-NL')}
                  ${this.currentViewStock.unit}
                </div>
              </div>
              <div class="row centered display-props">
                <div class="label">In pick order:</div>
                <div class="value">
                  ${roundUnitNumbers(
                    this.currentViewStock.in_order_amount_in_units,
                  ).toLocaleString('nl-NL')}
                  ${this.currentViewStock.unit}
                </div>
              </div>
              <div class="row centered display-props">
                <div class="label">In bestelling:</div>
                <div class="value">
                  ${roundUnitNumbers(
                    this.currentViewStock.to_be_delivered,
                  ).toLocaleString('nl-NL')}
                  ${this.currentViewStock.unit}
                </div>
              </div>
              <div class="row centered display-props">
                <div class="label">Status:</div>
                <div class="value">
                  ${this.renderStatus(this.currentViewStock)}
                </div>
              </div>
              <div class="row centered display-props">
                <langhout-button
                  text="Refresh voorraad"
                  @click="${() => {
                    this.refreshStockProduct();
                  }}"
                ></langhout-button>
              </div>
            </div>
            <div class="fill"></div>
            <div class="properties-column">
              <div class="row centered display-props">
                <div class="label">Voorraadproduct:</div>
                <div class="value">
                  <div class="row">
                    <langhout-checkbox
                      id="stockTypeCheckbox"
                      .checked=${
                        this.currentViewStock.stock_type !=
                        ProductStockTypeOnRequest
                      }
                    ></langhout-checkbox>
                  </div>
                </div>
              </div>
              <div class="row centered display-props">
                <div class="label">Voorraad</div>
                <div class="value row stock-display-margin">
                  <span class="dot dot-green"></span>
                </div>
              </div>
              <div class="row centered display-props">
                <div class="label">Voorraad beperkt</div>
                <div class="value row">
                  <span class="dot dot-orange"></span>
                  <input
                    value=${this.currentViewStock.limited_stock_amount}
                    type="number"
                    id="limitedStockAmount"
                    name="limitedStockAmount"
                  />
                </div>
              </div>
              <div class="row centered display-props">
                <div class="label">Niet op voorraad</div>
                <div class="value row">
                  <span class="dot dot-red"></span>
                  <input
                    value=${this.currentViewStock.not_in_stock_amount}
                    type="number"
                    id="notInStockAmount"
                    name="notInStockAmount"
                  />
                </div>
              </div>
              <div class="row centered display-props">
                <div class="label">Sales order beperkingen:</div>
                <div class="value row">
                  <string-array-input
                    inputType="number"
                    id="salesOrderRestrictions"
                    .values=${this.currentViewStock.sales_order_limit}
                  ></string-array-input>
                </div>
              </div>
              <div class="row centered display-props">
                <langhout-button
                  text="Wijzigen"
                  @click="${async () => {
                    this.disableButtons = true;
                    await this.updateStockDetails();
                    this.disableButtons = false;
                  }}"
                ></langhout-button>
              </div>
            </div>
          </div>
          <div class="row centered">
            <canvas id="stockChart"></canvas>
          </div>
        </div>
      </div>
    `;
  }

  renderStatus = (product: ProductStockView) => {
    if (product.stock_type === ProductStockTypeOnRequest) {
      return html`Op Aanvraag`;
    }
    if (product.stock_status === StockStatusInStock) {
      return html`
        <div class="row">
          <span class="dot dot-green"></span>
          Op voorraad
        </div>
      `;
    } else if (product.stock_status === StockStatusLimited) {
      return html`
        <div class="row">
          <span class="dot dot-orange"></span>
          Voorraad beperkt
        </div>
      `;
    } else {
      return html`
        <div class="row">
          <span class="dot dot-red"></span>
          Uitverkocht
        </div>
      `;
    }
  };

  getProduct = async () => {
    const result = await authenticatedAPICall(
      `/stock/products/${this.currentID}`,
      'GET',
      new Headers(),
    );
    if (isSuccessfulAPIResponse(result)) {
      this.currentViewStock = result.body;
      this.renderChart();
    }
  };

  refreshStockProduct = async () => {
    const result = await authenticatedAPICall(
      `/stock/products/${this.currentID}/refresh`,
      'PUT',
      new Headers(),
    );
    if (isSuccessfulAPIResponse(result)) {
      showNotification('Voorraad bijgewerkt');
      this.currentViewStock = result.body;
      this.renderChart();
    }
  };

  updateStockDetails = async () => {
    type UpdateStockDetailsPayload = {
      stock_type: number;
      limited_stock_amount: number;
      not_in_stock_amount: number;
      sales_order_limit: string[];
    };
    const updateStockDetailsPayload = <UpdateStockDetailsPayload>{};
    if (this.stockTypeCheckbox) {
      const isChecked = this.stockTypeCheckbox.checked;
      if (isChecked) {
        updateStockDetailsPayload.stock_type = ProductStockTypeStock;
      } else {
        updateStockDetailsPayload.stock_type = ProductStockTypeOnRequest;
      }
    } else {
      showNotification(
        'Unable to update stock details, please try again later',
      );
      return;
    }
    if (this.limitedStockInput) {
      if (this.limitedStockInput.value == '') {
        showNotification('Limited stock amount cannot be empty');
        return;
      }
      updateStockDetailsPayload.limited_stock_amount = parseInt(
        this.limitedStockInput.value,
      );
    } else {
      showNotification(
        'Unable to update stock details, please try again later',
      );
      return;
    }
    if (this.salesOrderLimitElement) {
      updateStockDetailsPayload.sales_order_limit =
        this.salesOrderLimitElement.values;
      this.currentViewStock.sales_order_limit =
        this.salesOrderLimitElement.values;
      for (const val of this.salesOrderLimitElement.values) {
        if (val == '') {
          showNotification('Sales order limits cannot contain empty values');
          return;
        }
        if (isNaN(parseInt(val))) {
          showNotification(
            `Sales order limits can only contain numbers, incorrect value: ${val}`,
          );
          return;
        }
        if (val.length < 6) {
          showNotification(
            `Sales order limits must be at least 6 digits long, incorrect value: ${val}`,
          );
          return;
        }
      }
    } else {
      showNotification(
        'Unable to update stock details, please try again later',
      );
      return;
    }

    if (this.outOfStockInput) {
      if (this.outOfStockInput.value == '') {
        showNotification('Out of stock amount cannot be empty');
        return;
      }
      updateStockDetailsPayload.not_in_stock_amount = parseInt(
        this.outOfStockInput.value,
      );
    } else {
      showNotification(
        'Unable to update stock details, please try again later',
      );
      return;
    }

    const result = await authenticatedAPICall(
      `/stock/products/${this.currentID}`,
      'PUT',
      new Headers(),
      JSON.stringify(updateStockDetailsPayload),
    );
    if (
      result.statusCode !== undefined &&
      result.statusCode >= 200 &&
      result.statusCode < 300
    ) {
      this.getProduct();
    }
  };

  private renderChart = () => {
    const stockDataAndLabels = this.generateStockDataAndLabels();
    const plannedProduction =
      this.generatePlannedProduction(stockDataAndLabels);
    const limitedStockData = this.generateLimitedStockData(stockDataAndLabels);
    const outOfStockData = this.generateOutOfStockData(stockDataAndLabels);
    if (this.chart) {
      this.chart.destroy();
    }

    if (this.chartCanvas) {
      this.chart = new Chart(this.chartCanvas, {
        type: 'line',
        data: {
          labels: stockDataAndLabels.labels,
          datasets: [
            {
              borderWidth: 3,
              pointRadius: 4,
              label: 'Voorraad',
              data: stockDataAndLabels.data,
              pointHoverRadius: 6,
              borderColor: '#014E2A',
              backgroundColor: '#014E2A',
              spanGaps: true,
            },
            {
              borderWidth: 3,
              pointRadius: 4,
              label: 'Voorraad planning',
              data: plannedProduction,
              pointHoverRadius: 6,
              borderColor: 'grey',
              backgroundColor: 'grey',
              spanGaps: true,
            },
            {
              borderWidth: 3,
              pointRadius: 0,
              label: 'Voorraad beperkt',
              data: limitedStockData,
              pointHoverRadius: 5,
              borderColor: 'orange',
              backgroundColor: 'orange',
              spanGaps: true,
            },
            {
              borderWidth: 3,
              pointRadius: 0,
              label: 'Niet op voorraad',
              data: outOfStockData,
              pointHoverRadius: 5,
              borderColor: 'red',
              backgroundColor: 'red',
              spanGaps: true,
            },
          ],
        },
        options: {
          responsive: true,
          elements: {
            point: {},
          },
          plugins: {
            tooltip: {
              boxPadding: 8,
              padding: 15,
              titleFont: {
                size: 20,
              },
              bodyFont: {
                size: 20,
              },
            },
            legend: {
              position: 'bottom',
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                lineWidth: context => {
                  if (context.tick.value === 0) {
                    return 3;
                  }
                  return 1;
                },
                color: context => {
                  if (context.tick.value === 0) {
                    return 'black';
                  }
                  return 'grey';
                },
              },
            },
          },
        },
      });
    }
  };

  private generatePlannedProduction = (stockData: LabelsAndData): number[] => {
    const result: number[] = [];
    let totalNumberToAdd = 0;
    for (let i = 0; i < stockData.labels.length; i++) {
      const curDate = stockData.dates[i];
      const plannedProduction = this.findPlannedProductionForDate(curDate);
      result.push(plannedProduction + totalNumberToAdd + stockData.data[i]);
      totalNumberToAdd += plannedProduction;
    }
    return result;
  };

  private findPlannedProductionForDate = (date: Date): number => {
    if (!this.currentViewStock.planned_production_over_time) {
      return 0;
    }
    const productionOverTime =
      this.currentViewStock.planned_production_over_time;
    for (let i = 0; i < productionOverTime.length; i++) {
      if (
        new Date(productionOverTime[i].date).toDateString() ==
        date.toDateString()
      ) {
        return productionOverTime[i].amount;
      }
    }

    return 0;
  };

  private generateLimitedStockData = (stockData: LabelsAndData): number[] => {
    const result: number[] = [];
    for (let i = 0; i < stockData.labels.length; i++) {
      result.push(this.currentViewStock.limited_stock_amount);
    }
    return result;
  };

  private generateOutOfStockData = (stockData: LabelsAndData): number[] => {
    const result: number[] = [];
    for (let i = 0; i < stockData.labels.length; i++) {
      result.push(this.currentViewStock.not_in_stock_amount);
    }
    return result;
  };

  private generateStockDataAndLabels = (): LabelsAndData => {
    if (!this.currentViewStock.delivery_over_time) {
      return this.generateDataNoDeliveries();
    } else {
      const startDate = this.getStartDate();
      const differenceInDays = this.getDaysToGenerate(startDate);

      let deliveryCounter = 0;
      let remainingStock = this.currentViewStock.stock_amount;
      const labels = [];
      const data = [];
      const dates = [];
      for (let i = 0; i < differenceInDays; i++) {
        const currentDelivery =
          this.currentViewStock.delivery_over_time[deliveryCounter];
        const nextDate = new Date(currentDelivery.date);
        if (this.haveEqualDate(startDate, nextDate)) {
          remainingStock =
            remainingStock - roundUnitNumbers(currentDelivery.amount_in_units);
          if (
            this.currentViewStock.delivery_over_time.length - 1 >
            deliveryCounter
          ) {
            deliveryCounter++;
          }
        }
        data.push(remainingStock);
        labels.push(`${startDate.getDate()}/${startDate.getMonth() + 1}`);
        dates.push(new Date(startDate));
        startDate.setDate(startDate.getDate() + 1);
      }
      return <LabelsAndData>{
        data: data,
        labels: labels,
        dates: dates,
      };
    }
  };

  getStartDate = (): Date => {
    let min = new Date();
    this.currentViewStock.delivery_over_time.forEach((e: DeliveryOverTime) => {
      if (new Date(e.date) < min) {
        min = new Date(e.date);
      }
    });

    return min;
  };

  private haveEqualDate = (date1: Date, date2: Date): boolean => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth()
    );
  };

  private getDaysToGenerate = (startDate: Date): number => {
    const endDate = this.getLastDate();

    const dateDifference = endDate.getTime() - startDate.getTime();
    return Math.max(dateDifference / (1000 * 3600 * 24) + 3, 14);
  };

  private getLastDate = (): Date => {
    let endDate = new Date(
      this.currentViewStock.delivery_over_time[
        this.currentViewStock.delivery_over_time.length - 1
      ].date,
    );
    endDate.setDate(endDate.getDate() + 1);
    if (
      this.currentViewStock.planned_production_over_time &&
      this.currentViewStock.planned_production_over_time.length > 0
    ) {
      const plannedProductionDate = new Date(
        this.currentViewStock.planned_production_over_time[
          this.currentViewStock.planned_production_over_time.length - 1
        ].date,
      );
      plannedProductionDate.setDate(plannedProductionDate.getDate() + 1);
      if (plannedProductionDate > endDate) {
        endDate = plannedProductionDate;
      }
    }
    return endDate;
  };

  private generateDataNoDeliveries = (): LabelsAndData => {
    const labels = [];
    const data = [];
    const dates = [];
    const date = new Date();
    for (let i = 0; i < 14; i++) {
      labels.push(`${date.getDate()}/${date.getMonth() + 1}`);
      dates.push(new Date(date));

      date.setDate(date.getDate() + 1);
      data.push(this.currentViewStock.stock_amount);
    }
    return <LabelsAndData>{
      data: data,
      labels: labels,
      dates: dates,
    };
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-view-stock': ViewStock;
  }
}
